import type { JwtClaims } from '@/types/auth';

/**
 * Lectura del JWT del backend (`backend/src/lib/jwt.ts` firma `{ sub, email }` con
 * `JWT_EXPIRES_IN`, por defecto `7d`).
 *
 * OJO: aquí NO se verifica la firma — eso es responsabilidad del backend en cada request.
 * El frontend solo decodifica el payload para saber si el token ya expiró y no mostrar
 * pantallas protegidas con un token muerto (evita el "flash" antes del 401).
 */

function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Decodifica el payload del JWT. Devuelve `null` si el token está malformado. */
export function decodeJwt(token: string): JwtClaims | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;

  const payload = segments[1];
  if (!payload) return null;

  try {
    const claims = JSON.parse(base64UrlDecode(payload)) as JwtClaims;
    if (!claims?.sub) return null;
    return claims;
  } catch {
    return null;
  }
}

/**
 * ¿El token expiró? `skewSeconds` da un margen de seguridad para no iniciar una petición
 * con un token que expira en milisegundos.
 *
 * Un token sin `exp` (no es el caso del backend, pero podría pasar) se considera vigente:
 * el backend lo rechazará con 401 y el handler global limpia la sesión.
 */
export function isTokenExpired(token: string | null | undefined, skewSeconds = 30): boolean {
  if (!token) return true;

  const claims = decodeJwt(token);
  if (!claims) return true;
  if (typeof claims.exp !== 'number') return false;

  return claims.exp * 1000 <= Date.now() + skewSeconds * 1000;
}

/** Fecha de expiración del token (útil para mostrar "sesión válida hasta…"). */
export function getTokenExpiration(token: string | null | undefined): Date | null {
  if (!token) return null;
  const claims = decodeJwt(token);
  if (!claims || typeof claims.exp !== 'number') return null;
  return new Date(claims.exp * 1000);
}
